<script lang="ts" setup>
  import type { Maybe, RefComponent, SettingFooterValue } from '@/types'
  import type { CustomerPopupSubscription } from '@/types/customer/subscriptions'
  import { SubscriptionSource } from '@/types'

  import { computed, nextTick, ref, watch } from 'vue'
  import { useRoute } from 'vue-router'
  import { useBreakpoints } from '@vueuse/core'

  import { storeToRefs } from 'pinia'
  import { useCustomerSubscriptionsStore } from '@/stores/customer/subscriptions'

  import {
    checkIfExternal,
    isEmpty,
    prepareExternalUrl,
    prepareMenuUrl,
    validateEmail,
  } from '@/helpers'

  import { useCustomerSettings } from '@/composables/useCustomerSettings'

  import PopupSubscriptionModel from '@/models/PopupSubscriptionModel'

  import CustomerWrapper from '@/components/Partials/Customer/CustomerWrapper.vue'
  import CustomerFooterColumnTitle from '@/components/Global/Customer/CustomerFooterColumnTitle.vue'
  import ManagerInput from '@/components/Global/Manager/Forms/ManagerInput.vue'
  import ManagerTextButton from '@/components/Global/Manager/Forms/ManagerTextButton.vue'
  import ManagerIcon from '@/components/Global/Manager/ManagerIcon.vue'
  import { goal } from "@/plugins/metrika";

  const { currentSettings: footerSettings } =
    useCustomerSettings<SettingFooterValue>('footer')

  const groups = computed(() => {
    const arr = [...(footerSettings.value?.value.groups ?? [])]

    arr.splice(arr.length - 1, 0, { name: 'Контакты', type: 'form', items: [] })

    return arr
  })
  const contacts = computed(() => footerSettings.value?.value.contacts ?? '')

  const breakpoints = useBreakpoints({
    lg: 1024,
  })
  const isDesktop = breakpoints.greaterOrEqual('lg')
  /**
   * Form
   * */
  const email = ref<Maybe<string>>(null)
  const emailInputRef: RefComponent<typeof ManagerInput> = ref(null)
  const subscribed = ref(false)

  const customerSubscriptionsStore = useCustomerSubscriptionsStore()
  const { singleItemIsLoading: subscribing } = storeToRefs(
    customerSubscriptionsStore
  )

  const remoteValidation = ref<Record<string, string[]>>({})

  async function submit() {
    resetErrors()

    if (!(await emailInputRef.value?.validate()) || subscribing.value) return

    const { error, validationErrors } =
      await customerSubscriptionsStore.subscribe<CustomerPopupSubscription>(
        PopupSubscriptionModel.mergeTogether({
          source: SubscriptionSource.Footer,
          email: email.value,
        })
      )

    if (error && !isEmpty(validationErrors)) {
      resetErrors(validationErrors)
    } else {
      subscribed.value = true
      goal('subscription')
    }
  }

  watch(
    () => email.value,
    async (value, oldValue) => {
      if ((oldValue?.length ?? 0) > (value?.length ?? 0) && subscribed.value) {
        subscribed.value = false
      }
    }
  )

  const route = useRoute()

  watch(
    () => route.path,
    async () => {
      if (!email.value && !subscribed.value) return

      await reset()
    }
  )

  async function reset() {
    resetErrors()

    email.value = null
    subscribed.value = false

    await nextTick()

    emailInputRef.value?.resetLazyValidation()
  }

  function resetErrors(validation = {}) {
    remoteValidation.value = validation
  }
</script>

<template>
  <CustomerWrapper class="mt-[56px] lg:mt-[72px]">
    <!-- Footer -->
    <div class="border-t border-solid border-black">
      <div
        class="flex flex-wrap gap-6 sm:gap-12 lg:gap-5 justify-between mt-10"
      >
        <template v-if="!isDesktop">
          <div class="shrink-0 basis-full md:basis-[calc(50%-3rem)]">
            <CustomerFooterColumnTitle class="!mb-2">
              Рассылка
            </CustomerFooterColumnTitle>

            <div id="subscription" class="text-cxs lg:text-csm">
              Подпишитесь на&nbsp;рассылку о&nbsp;новинках, акциях
              и&nbsp;скидках
            </div>
          </div>

          <div
            class="flex items-center shrink-0 basis-full md:basis-[calc(50%-3rem)] -mt-6 md:mt-0"
          >
            <div class="w-full">
              <ManagerInput
                ref="emailInputRef"
                v-model="email"
                aria-labelled-by="subscription"
                title="Введите эл. почту"
                name="email"
                type="email"
                class="!pt-0"
                input-classes="!text-cxs lg:!text-csm !h-[37px] lg:!h-[39px]"
                dynamic-placeholder-classes="text-cxs lg:text-csm"
                error-position="left"
                :errors="[validateEmail, ...(remoteValidation['email'] ?? [])]"
                error-absolute
                manual-validation
                autocomplete="on"
                :prompt="subscribed && email ? 'Ваша подписка оформлена' : ''"
                prompt-absolute
                prompt-classes="text-cgreen"
                @keydown.enter="submit"
              >
                <template v-if="email" #append-icon="{ error }">
                  <svg
                    v-if="error"
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M3 13L8 8M8 8L13 3M8 8L13 13M8 8L3 3M15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8Z"
                      stroke="#FE5D2A"
                      stroke-width="1.2"
                    />
                  </svg>

                  <svg
                    v-else-if="subscribed"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M14 3L6.15789 13L2 8.71429"
                      stroke="#1F1F1F"
                      stroke-width="1.2"
                    />
                  </svg>
                </template>
              </ManagerInput>
            </div>

            <div class="ml-3 shrink-0">
              <ManagerTextButton class="p-1" @click="submit">
                <ManagerIcon
                  class="rotate-180"
                  icon="arrow-left"
                  color="mblack"
                />
              </ManagerTextButton>
            </div>
          </div>
        </template>

        <div
          v-for="(group, groupIndex) in groups"
          :key="`group-${groupIndex}`"
          class="shrink-0 basis-[calc(50%-3rem)] lg:basis-[calc(16.666667%-1.25rem)]"
        >
          <CustomerFooterColumnTitle class="whitespace-nowrap">
            {{ group.name }}
          </CustomerFooterColumnTitle>

          <div v-if="group.items.length" class="text-cxs lg:text-csm">
            <div
              v-for="(item, index) in group.items"
              :key="`menu-item-${index}-${groupIndex}`"
              class="mb-3 last:mb-0 whitespace-nowrap"
            >
              <router-link
                v-if="!checkIfExternal(item.url)"
                :to="prepareMenuUrl(item.url)"
                class="block hover:opacity-60"
                :class="[
                  {
                    'font-bold': item.bold,
                  },
                ]"
                :style="`color: ${item.color};`"
              >
                {{ item.name }}
              </router-link>

              <a
                v-else
                :href="prepareExternalUrl(item.url)"
                class="block hover:opacity-60 external-link"
                :class="[
                  {
                    'font-bold': item.bold,
                  },
                ]"
                :style="`color: ${item.color};`"
                target="_blank"
              >
                {{ item.name }}
              </a>
            </div>
          </div>

          <div
            v-else-if="group.type === 'form'"
            class="text-cxs lg:text-csm whitespace-nowrap -mt-[0.4rem] !leading-[1.9rem]"
            v-html="contacts"
          />
        </div>

        <div
          v-if="isDesktop"
          class="shrink-0 basis-[calc((16.666667%*2)-1.5rem)] flex flex-col"
        >
          <CustomerFooterColumnTitle>Рассылка</CustomerFooterColumnTitle>

          <div class="text-cxs lg:text-csm">
            Подпишитесь на&nbsp;рассылку о&nbsp;новинках, акциях и&nbsp;скидках
          </div>

          <div class="mt-auto flex items-center">
            <div class="w-full">
              <ManagerInput
                ref="emailInputRef"
                v-model="email"
                title="Введите эл. почту"
                name="email"
                type="email"
                input-classes="!text-cxs lg:!text-csm !h-[37px] lg:!h-[39px]"
                dynamic-placeholder-classes="text-cxs lg:text-csm"
                error-position="left"
                :errors="[validateEmail, ...(remoteValidation['email'] ?? [])]"
                error-absolute
                manual-validation
                autocomplete="on"
                :prompt="subscribed && email ? 'Ваша подписка оформлена' : ''"
                prompt-absolute
                prompt-classes="text-cgreen"
                @keydown.enter="submit"
              >
                <template v-if="email" #append-icon="{ error }">
                  <svg
                    v-if="error"
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M3 13L8 8M8 8L13 3M8 8L13 13M8 8L3 3M15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8Z"
                      stroke="#FE5D2A"
                      stroke-width="1.2"
                    />
                  </svg>

                  <svg
                    v-else-if="subscribed"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M14 3L6.15789 13L2 8.71429"
                      stroke="#1F1F1F"
                      stroke-width="1.2"
                    />
                  </svg>
                </template>
              </ManagerInput>
            </div>

            <div class="ml-3 shrink-0">
              <ManagerTextButton
                aria-label="Оформить подписку"
                class="p-1"
                @click="submit"
              >
                <ManagerIcon
                  class="rotate-180"
                  icon="arrow-left"
                  color="mblack"
                />
              </ManagerTextButton>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Sub-footer -->
    <div
      class="flex items-center flex-col md:flex-row gap-1 md:gap-3 text-c2.5xs lg:text-cxs py-4 mt-10 lg:mt-[112px]"
    >
      <!-- Copy -->
      <div>&copy; SEEN {{ new Date().getFullYear() }}</div>

      <!-- Links -->
      <div>
        <router-link :to="{ name: 'customer-terms' }">
          Пользовательское соглашение
        </router-link>
      </div>

      <div>
        <router-link :to="{ name: 'customer-privacy-policy' }">
          Политика конфиденциальности
        </router-link>
      </div>
    </div>
  </CustomerWrapper>
</template>
